"use client"

import { useEffect, useRef, type ReactNode, type Ref } from "react"
import { ChevronRight, ExternalLink, Info } from "lucide-react"

import type { KpiCardProps } from "@/components/ui/analytics/kpi-card"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CollapsibleTrigger } from "@/components/ui/collapsible"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { computeShareTrend, type MetricKind } from "@/lib/analytics/deltas"
import { formatDuration, formatShare } from "@/lib/analytics/format"
import {
  MIN_SHARE_POPULATION,
  NO_VALUE,
  share,
  type ShareCounts,
} from "@/lib/conversation-quality/metric-definitions"
import { CHANNEL_LABELS } from "@/lib/agents/messaging/types"
import type { ConversationReference } from "@/lib/conversation-quality/sources/types"
import { cn } from "@/lib/utils"

/** A landing's target, scrolled to clear of the bar a phone pins above the page. */
export const LANDING_SCROLL_MARGIN = "scroll-mt-20 md:scroll-mt-0"

/** Where a waiting draft is listed, so the question it was drafted for can bring it into view. */
export function suggestionElementId(id: string): string {
  return `quality-suggestion-${id}`
}

export function revealSuggestion(id: string) {
  document.getElementById(suggestionElementId(id))?.scrollIntoView({ behavior: "smooth", block: "center" })
}

/**
 * A card a link opened the tab to land on: scrolled into view once its data is in, then the landing let go. `reveal`
 * brings a part of it into view instead, when it can.
 */
export function useLandedScroll<T extends HTMLElement>(
  landing: boolean,
  loaded: boolean,
  onLanded: () => void,
  reveal?: () => boolean
): Ref<T> {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (!landing || !loaded) return
    if (!reveal?.()) ref.current?.scrollIntoView({ block: "start" })
    onLanded()
  }, [landing, loaded, onLanded, reveal])
  return ref
}

/** A metric's name that carries its definition, so a label cannot drift from what it means. */
export function DefinedTerm({ label, definition }: { label: string; definition: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-left underline decoration-dotted underline-offset-4"
          >
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{definition}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** The definition behind an icon — for a card title, which must stay plain text. */
export function InfoTip({ label, definition }: { label: string; definition: string }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${label}: definition`}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {definition}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** One card of the page: a plain title, and beside it only what the title cannot say. */
export function SectionCard({
  ref,
  title,
  definition,
  subtitle,
  aside,
  className,
  children,
}: {
  ref?: Ref<HTMLDivElement>;
  title: string;
  definition?: string;
  subtitle?: ReactNode;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card ref={ref} className={cn("min-w-0", className)}>
      <CardHeader className="space-y-0 p-4 pb-0 sm:p-6 sm:pb-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <CardTitle className="flex items-center gap-2 text-base leading-6">
              <span>{title}</span>
              {definition && <InfoTip label={title} definition={definition} />}
            </CardTitle>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {aside && <div className="shrink-0">{aside}</div>}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-3 sm:p-6 sm:pt-4">{children}</CardContent>
    </Card>
  )
}

/**
 * A share tile's change, «up from 40%» — only over a previous window holding enough to share. `previous` is null where
 * no previous window exists.
 */
export function shareComparison(
  current: number | null,
  previous: ShareCounts | null,
  kind: MetricKind
): Pick<KpiCardProps, "delta" | "baseline"> {
  if (current === null || previous === null) return {}
  if (previous.over < MIN_SHARE_POPULATION) return { baseline: "too few sessions before to compare" }
  return { delta: computeShareTrend(current, previous.of / previous.over, kind) }
}

/** A row that opens: a chevron that turns while open, then the row's own heading. */
export function RowTrigger({ children }: { children: ReactNode }) {
  return (
    <CollapsibleTrigger className="group flex w-full items-start gap-2 text-left">
      <ChevronRight
        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
        aria-hidden="true"
      />
      {children}
    </CollapsibleTrigger>
  )
}

export function EmptyNote({ text = "No activity in this period." }: { text?: string }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{text}</p>
}

/** What a card's numbers leave out, said under them. */
export function CountedApart({ count, one, many }: { count: number; one: string; many: string }) {
  if (count === 0) return null

  return (
    <p className="text-xs text-muted-foreground">{count === 1 ? one : `${count.toLocaleString()} ${many}`}</p>
  )
}

export function duration(ms: number | null): string {
  return formatDuration(ms, NO_VALUE)
}

export function sessionCount(sessions: number): string {
  return sessions === 1 ? "1 session" : `${sessions.toLocaleString()} sessions`
}

export function requestTotal(requests: number): string {
  return requests === 1 ? "1 request" : `${requests.toLocaleString()} requests`
}

export function requestsOf({ of, over }: ShareCounts): string {
  return `${of.toLocaleString()} of ${requestTotal(over)}`
}

/**
 * «Sep 8 – 14», «Sep 28 – Oct 4»: the window's days. Its last day holds the instant before the end, which is now for a
 * preset and 23:59:59 of the chosen day for a custom range.
 */
export function formatWindow(range: { start: string; end: string }, timezone: string): string {
  const format = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", timeZone: timezone })
  const part = (at: number, type: "month" | "day") =>
    format.formatToParts(new Date(at)).find((p) => p.type === type)?.value ?? ""
  const start = Date.parse(range.start)
  const last = Date.parse(range.end) - 1

  if (part(start, "month") === part(last, "month")) {
    const days = part(start, "day") === part(last, "day") ? part(start, "day") : `${part(start, "day")} – ${part(last, "day")}`
    return `${part(start, "month")} ${days}`
  }
  return `${part(start, "month")} ${part(start, "day")} – ${part(last, "month")} ${part(last, "day")}`
}

/** Every link into a conversation opens beside the page, so the reader keeps their place in it. */
const IN_NEW_TAB = { target: "_blank", rel: "noopener noreferrer" } as const

/** A listed conversation opens by its contact's name — the one way in, wherever a row names one. */
export function ConversationNameLink({ href, name }: { href: string; name: string }) {
  return (
    <a
      href={href}
      {...IN_NEW_TAB}
      title={name}
      className="-mx-1 inline-flex min-w-0 max-w-full items-center gap-1 rounded px-1 py-0.5 text-sm font-medium text-primary hover:bg-accent hover:underline"
    >
      <span className="truncate">{name}</span>
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
    </a>
  )
}

/** A conversation to open, named by its session where one holds it. */
type LinkedReference = ConversationReference & { sessionNumber: number | null }

export function SessionLinks({ label, sessions }: { label: string; sessions: LinkedReference[] }) {
  if (sessions.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="mr-0.5 text-muted-foreground">{label}</span>
      {sessions.map((session) => (
        <a
          key={`${session.href}|${session.sessionNumber}`}
          href={session.href}
          {...IN_NEW_TAB}
          className="inline-flex h-6 max-w-full items-center truncate rounded-full border bg-background px-2.5 font-medium transition-colors hover:bg-accent"
        >
          {referenceLabel(session)}
        </a>
      ))}
    </div>
  )
}

export function referenceLabel({ contactTitle, sessionNumber }: LinkedReference): string {
  return sessionNumber === null ? contactTitle : `${contactTitle} · Session #${sessionNumber}`
}

export function channelLabel(channel: string): string {
  return CHANNEL_LABELS[channel as keyof typeof CHANNEL_LABELS] ?? channel
}

export interface Segment {
  key: string;
  label: string;
  count: number;
  color: string;
  /** Shown behind the label, where a colour alone would leave the reader guessing. */
  definition?: string;
}

/** One bar split by share — it reads at any width, with no axis to crowd. */
export function StackedBar({
  segments,
  total,
  className,
}: {
  segments: Segment[];
  total: number;
  className?: string;
}) {
  return (
    <div className={cn("flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-muted", className)}>
      {segments
        .filter((segment) => segment.count > 0)
        .map((segment) => (
          <div
            key={segment.key}
            title={`${segment.label} — ${segment.count.toLocaleString()}`}
            style={{ width: `${(segment.count / total) * 100}%`, background: segment.color }}
          />
        ))}
    </div>
  )
}

/** The bar's segments as rows: count and share, every segment listed whether or not it drew. */
export function SegmentLegend({ segments, total }: { segments: Segment[]; total: number }) {
  return (
    <ul className="space-y-1.5">
      {segments.map((segment) => (
        <li key={segment.key} className="flex items-center gap-3 text-sm">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: segment.color }} />
          <span className="min-w-0 flex-1">
            <SegmentLabel label={segment.label} definition={segment.definition} />
          </span>
          <span className="font-medium tabular-nums">{segment.count.toLocaleString()}</span>
          <span className="w-12 text-right text-muted-foreground tabular-nums">
            {formatShare(share(segment.count, total), NO_VALUE)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** The bar's colours named in one line — for bars that share one key, or a row that carries its own. */
export function SegmentKey({ segments }: { segments: Segment[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {segments.map((segment) => (
        <li key={segment.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: segment.color }} />
          <SegmentLabel label={segment.label} definition={segment.definition} />
        </li>
      ))}
    </ul>
  )
}

function SegmentLabel({ label, definition }: Pick<Segment, "label" | "definition">) {
  return definition ? <DefinedTerm label={label} definition={definition} /> : <span>{label}</span>
}

/** Rows of one measure, each with its share of the rows' total drawn beneath it. */
export function RankedBars({
  rows,
}: {
  rows: { key: string; label: string; count: number; footer?: ReactNode }[];
}) {
  const total = rows.reduce((sum, row) => sum + row.count, 0)

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.key} className="space-y-1">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>{row.label}</span>
            <span className="font-medium tabular-nums">{row.count.toLocaleString()}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${total > 0 ? (row.count / total) * 100 : 0}%` }}
            />
          </div>
          {row.footer}
        </div>
      ))}
    </div>
  )
}
